import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { CurateWordmark } from "@/components/Brand";
import OnboardingModal from "@/components/OnboardingModal";
import { LayoutDashboard, Boxes, MessageSquareQuote, Tv2, BarChart3, Building2, LogOut, MapPin } from "lucide-react";

const NAV = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Overview", end: true, testid: "nav-overview" },
  { to: "/dashboard/brand", icon: Building2, label: "Brand info", testid: "nav-brand" },
  { to: "/dashboard/catalog", icon: Boxes, label: "Catalog", testid: "nav-catalog" },
  { to: "/dashboard/locations", icon: MapPin, label: "Locations", testid: "nav-locations" },
  { to: "/dashboard/questions", icon: MessageSquareQuote, label: "Questions", testid: "nav-questions" },
  { to: "/dashboard/experiences", icon: Tv2, label: "Experiences", testid: "nav-experiences" },
  { to: "/dashboard/analytics", icon: BarChart3, label: "Analytics", testid: "nav-analytics" },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="dashboard-shell min-h-screen flex bg-[#fafafa]">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 bg-[#0F1210] text-white flex flex-col">
        <div className="px-6 h-16 flex items-center border-b border-white/10">
          <CurateWordmark size={26} link={false} onDark />
        </div>
        <nav className="flex-1 px-3 py-6 space-y-1">
          {NAV.map(({ to, icon: Icon, label, end, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 h-10 text-sm transition-colors ${
                  isActive ? "bg-white text-[#0a0a0a] font-medium" : "text-white/70 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="px-3 py-3">
            <div className="label-overline text-white/40">Signed in as</div>
            <div className="mt-1 text-sm font-medium truncate" data-testid="current-user-email">{user?.email}</div>
            <div className="text-xs text-white/50 truncate">{user?.brand_name}</div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full flex items-center gap-2 px-3 h-10 text-sm text-white/70 hover:bg-white/5 hover:text-white"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>

      {/* First-time onboarding (auto-shows up to 4 times) */}
      <OnboardingModal />
    </div>
  );
}
